'use client';

import { useEffect, useRef, useState } from 'react';

import { useRouter } from 'next/navigation';

import {
  getCustomerProfile,
  requestOtp,
  requestPhoneOtp,
  updateCustomerProfile,
} from '@/actions';
import {
  formatDocument,
  formatPhone,
  useOtpVerificationModal,
  type OtpVerifyResult,
} from '@/components';
import { useWhitelabelConfig } from '@/hooks/useWhitelabelConfig';
import { toast, useForm, useWatch, zodResolver } from '@/libs';
import { searchAddressByCep } from '@/services';
import { useCustomerStore, useModalStore } from '@/stores';
import { BRAZILIAN_STATES_OPTIONS, OtpScope, ValidationKey } from '@/types/enums';
import {
  OTP_HEADER_CURRENT_PHONE,
  OTP_HEADER_NEW_PHONE,
  customerProfileFormSchema,
  type CustomerProfile,
  type CustomerProfileFormInput,
  type CustomerProfileFormData,
  type OtpChallenge,
  type OtpHeaders,
} from '@/types/models';
import {
  formatCEP,
  formatState,
  isValidCEP,
  logger,
  parseBoolean,
  parseDigits,
} from '@/utils';
import {
  clearCachedCustomerProfile,
  getCachedCustomerProfile,
  getOrLoadCustomerProfile,
  isCachedProfileEmailStale,
  setCachedCustomerProfile,
} from '@/utils/customerProfileCache';
import { addDDIToPhone, removeDDIFromAutoInsert } from '@/utils/formatters';

type ProfileFormSeed = {
  customerId: string;
  document: string;
  name?: string;
  socialName?: string;
  email?: string;
  phone?: string;
};

type ProfileOtpContext = {
  data: CustomerProfileFormData;
  profile: CustomerProfile;
  hasPhoneChanged: boolean;
  otpHeaders: OtpHeaders;
};

type PendingOtpChallenge = {
  challenge: OtpChallenge;
  context: ProfileOtpContext;
};

const normalizePhoneForComparison = (phone: string | undefined): string =>
  removeDDIFromAutoInsert(parseDigits(phone || ''));

const buildDefaultValues = ({
  customerId = '',
  document = '',
}: Partial<ProfileFormSeed> = {}): CustomerProfileFormInput => ({
  customerId,
  fullname: '',
  hasSocialName: false,
  socialName: '',
  document,
  phone: '',
  phoneConfirmation: '',
  email: '',
  confirmEmail: '',
  marketingOptIn: false,
  zipCode: '',
  state: '',
  city: '',
  neighborhood: '',
  street: '',
  number: '',
  complement: '',
});

const mapProfileToForm = (
  profile: CustomerProfile | null,
  fallback: ProfileFormSeed,
): CustomerProfileFormInput => {
  const socialName = profile?.socialName?.trim() || fallback.socialName?.trim() || '';
  const email = profile?.email?.trim() || fallback.email?.trim() || '';
  const phone = formatPhone(profile?.phone || fallback.phone || '');

  return {
    customerId: fallback.customerId,
    fullname: profile?.name || fallback.name || '',
    hasSocialName: Boolean(socialName),
    socialName,
    document: formatDocument(fallback.document || ''),
    phone,
    phoneConfirmation: phone,
    email,
    confirmEmail: email,
    marketingOptIn: Boolean(profile?.marketingOptIn),
    zipCode: formatCEP(profile?.address?.cep || ''),
    state: formatState(profile?.address?.state || ''),
    city: profile?.address?.city || '',
    neighborhood: profile?.address?.district || '',
    street: profile?.address?.street || '',
    number: profile?.address?.number || '',
    complement: profile?.address?.complement || '',
  };
};

const mapFormToProfile = (data: CustomerProfileFormData): CustomerProfile => ({
  socialName: data.hasSocialName ? data.socialName || null : null,
  email: data.email,
  phone: data.phone,
  marketingOptIn: data.marketingOptIn,
  address: {
    cep: data.zipCode,
    state: data.state,
    city: data.city,
    district: data.neighborhood,
    street: data.street,
    number: data.number,
    complement: data.complement || undefined,
  },
});

export const useEditUserProfile = () => {
  const router = useRouter();
  const customerData = useCustomerStore((state) => state.customerData);
  const setCustomerData = useCustomerStore((state) => state.setCustomerData);
  const clearCustomerData = useCustomerStore((state) => state.clearCustomerData);
  const closeModal = useModalStore((state) => state.closeModal);
  const modalId = useModalStore((state) => state.modals[0]?.id);
  const { getValue } = useWhitelabelConfig();
  const { openOtpVerificationModal } = useOtpVerificationModal();

  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSearchingCep, setIsSearchingCep] = useState(false);
  const lastSearchedCepRef = useRef('');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingOtpChallengeRef = useRef<PendingOtpChallenge | null>(null);

  const defaultValues = buildDefaultValues({
    customerId: customerData?.customerId || '',
    document: customerData?.document || '',
  });
  const {
    control,
    handleSubmit,
    formState: { dirtyFields, errors, isValid },
    reset,
    setValue,
    trigger,
    watch,
  } = useForm<CustomerProfileFormInput, unknown, CustomerProfileFormData>({
    mode: 'onChange',
    resolver: zodResolver(customerProfileFormSchema),
    defaultValues,
  });

  const watchZipCode = (useWatch({ control, name: 'zipCode' }) ?? '') as string;
  const watchHasSocialName = Boolean(watch('hasSocialName'));

  useEffect(() => {
    let isMounted = true;

    const loadProfile = async () => {
      const fallbackProfile: ProfileFormSeed = {
        customerId: customerData?.customerId || '',
        document: customerData?.document || '',
        name: customerData?.name || '',
        socialName: customerData?.socialName || '',
        email: customerData?.email || '',
        phone: customerData?.phone || '',
      };

      if (!fallbackProfile.customerId) {
        if (!isMounted) return;
        reset(mapProfileToForm(null, fallbackProfile));
        setIsLoadingProfile(false);
        return;
      }

      try {
        const cachedProfile = getCachedCustomerProfile(fallbackProfile.customerId);

        if (
          isCachedProfileEmailStale(cachedProfile, fallbackProfile.email) &&
          fallbackProfile.customerId
        ) {
          clearCachedCustomerProfile(fallbackProfile.customerId);
        }

        const freshCachedProfile = getCachedCustomerProfile(fallbackProfile.customerId);
        const loadedProfile = freshCachedProfile
          ? freshCachedProfile
          : await getOrLoadCustomerProfile(fallbackProfile.customerId, () =>
              getCustomerProfile(fallbackProfile.customerId),
            );

        if (!isMounted) return;
        lastSearchedCepRef.current = parseDigits(loadedProfile?.address?.cep || '');
        reset(mapProfileToForm(loadedProfile, fallbackProfile));
      } catch (error) {
        logger.error('Erro ao carregar dados do perfil para edicao:', error);

        if (!isMounted) return;
        lastSearchedCepRef.current = '';
        reset(mapProfileToForm(null, fallbackProfile));
      } finally {
        if (isMounted) {
          setIsLoadingProfile(false);
        }
      }
    };

    loadProfile();

    return () => {
      isMounted = false;
    };
  }, [
    customerData?.customerId,
    customerData?.document,
    customerData?.email,
    customerData?.name,
    customerData?.phone,
    customerData?.socialName,
    reset,
  ]);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const cleanCep = parseDigits(watchZipCode);
    if (
      !dirtyFields.zipCode ||
      cleanCep.length !== 8 ||
      !isValidCEP(cleanCep) ||
      cleanCep === lastSearchedCepRef.current
    ) {
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setIsSearchingCep(true);
        const address = await searchAddressByCep(cleanCep);

        setValue('zipCode', formatCEP(address.zipCode), { shouldValidate: true });
        setValue('state', formatState(address.state), { shouldValidate: true });
        setValue('city', address.city, { shouldValidate: true });
        setValue('neighborhood', address.neighborhood, { shouldValidate: true });
        setValue('street', address.street, { shouldValidate: true });

        lastSearchedCepRef.current = cleanCep;
        await trigger(['state', 'city', 'neighborhood', 'street']);
      } catch (error) {
        lastSearchedCepRef.current = '';
        logger.error('Erro ao buscar CEP no perfil do usuario:', error);
        toast.error('Confirme o CEP antes de tentar novamente.');
      } finally {
        setIsSearchingCep(false);
      }
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [dirtyFields.zipCode, setValue, trigger, watchZipCode]);

  const handleCepBlur = async () => {
    const cleanCep = parseDigits(watchZipCode);
    if (
      !dirtyFields.zipCode ||
      cleanCep.length !== 8 ||
      cleanCep === lastSearchedCepRef.current
    ) {
      return;
    }

    try {
      setIsSearchingCep(true);
      const address = await searchAddressByCep(cleanCep);

      setValue('zipCode', formatCEP(address.zipCode), { shouldValidate: true });
      setValue('state', formatState(address.state), { shouldValidate: true });
      setValue('city', address.city, { shouldValidate: true });
      setValue('neighborhood', address.neighborhood, { shouldValidate: true });
      setValue('street', address.street, { shouldValidate: true });

      lastSearchedCepRef.current = cleanCep;
      await trigger(['state', 'city', 'neighborhood', 'street']);
    } catch (error) {
      lastSearchedCepRef.current = '';
      logger.error('Erro ao buscar CEP no blur do perfil do usuario:', error);
      toast.error('Confirme o CEP antes de tentar novamente.');
    } finally {
      setIsSearchingCep(false);
    }
  };

  const handleClose = () => {
    if (modalId) {
      closeModal(modalId);
    }
  };

  const handleSuccessfulUpdate = async ({
    data,
    profile,
    hasPhoneChanged,
  }: Omit<ProfileOtpContext, 'otpHeaders'>) => {
    if (!customerData?.customerId) return;

    clearCachedCustomerProfile(customerData.customerId);

    if (hasPhoneChanged) {
      clearCustomerData();
      toast.success(
        'Telefone atualizado com sucesso. Faça login novamente para continuar.',
      );
      handleClose();
      router.push('/');
      return;
    }

    setCachedCustomerProfile(customerData.customerId, profile);

    setCustomerData({
      ...customerData,
      socialName: data.hasSocialName ? data.socialName : undefined,
      email: data.email,
      phone: data.phone,
      hasMail: true,
      hasAddress: true,
    });

    toast.success('Dados cadastrais atualizados com sucesso');
    handleClose();
  };

  const handleUnavailableCurrentPhone = (message?: string) => {
    toast.error(
      message ||
        'Não há telefone atual válido para confirmar. Use a recuperação de telefone.',
    );
    handleClose();
  };

  const requestProfileOtp = async (
    challenge: OtpChallenge,
    context: ProfileOtpContext,
  ): Promise<string | undefined> => {
    if (!customerData?.customerId || !challenge.header) return undefined;

    if (challenge.header === OTP_HEADER_CURRENT_PHONE) {
      const result = await requestOtp({
        scope: OtpScope.PROFILE_UPDATE,
        customerId: customerData.customerId,
      });
      return result.data ? result.data.maskedPhone || challenge.maskedPhone : undefined;
    }

    if (challenge.header === OTP_HEADER_NEW_PHONE) {
      const result = await requestPhoneOtp({
        scope: OtpScope.PHONE_CHANGE,
        customerId: customerData.customerId,
        newPhone: addDDIToPhone(context.data.phone),
      });
      return result.data ? result.data.maskedPhone || challenge.maskedPhone : undefined;
    }

    return undefined;
  };

  const getOtpModalTitle = (challenge: OtpChallenge) =>
    challenge.header === OTP_HEADER_NEW_PHONE
      ? 'Confirme o novo telefone'
      : 'Confirme seu telefone atual';

  const getOtpModalDescription = (challenge: OtpChallenge) => (maskedPhone?: string) => {
    const target =
      challenge.header === OTP_HEADER_NEW_PHONE ? 'novo telefone' : 'telefone atual';

    return maskedPhone
      ? `Digite o código enviado para o ${target} ${maskedPhone}.`
      : `Digite o código enviado para o ${target}.`;
  };

  const verifyProfileOtp = async (
    code: string,
    challenge: OtpChallenge,
    context: ProfileOtpContext,
  ): Promise<OtpVerifyResult> => {
    if (!customerData?.customerId || !challenge.header) return 'error';

    const sanitizedCode = parseDigits(code);
    const otpHeaders: OtpHeaders = {
      ...context.otpHeaders,
      [challenge.header]: sanitizedCode,
    };

    const result = await updateCustomerProfile(
      customerData.customerId,
      context.profile,
      otpHeaders,
    );

    if (result.success) {
      pendingOtpChallengeRef.current = null;
      await handleSuccessfulUpdate(context);
      return 'success';
    }

    const resultCode = result.otpChallenge?.code || '';

    if (resultCode === 'OTP_A_UNAVAILABLE_NO_PHONE') {
      pendingOtpChallengeRef.current = null;
      handleUnavailableCurrentPhone(result.error);
      return 'success';
    }

    if (result.status === 403 || resultCode.includes('INVALID')) {
      return 'invalid';
    }

    if (result.status === 429 || resultCode.includes('MAX_ATTEMPTS')) {
      return 'blocked';
    }

    if (result.status === 428 && result.otpChallenge?.header) {
      pendingOtpChallengeRef.current = {
        challenge: result.otpChallenge,
        context: {
          ...context,
          otpHeaders,
        },
      };
      return 'success';
    }

    toast.error(result.error);
    return 'error';
  };

  const openProfileOtpChallenge = async (
    challenge: OtpChallenge,
    context: ProfileOtpContext,
  ): Promise<boolean> => {
    if (challenge.code === 'OTP_A_UNAVAILABLE_NO_PHONE') {
      handleUnavailableCurrentPhone(challenge.message);
      return true;
    }

    if (!challenge.header) {
      toast.error(challenge.message || 'Não foi possível confirmar o código.');
      return true;
    }

    const maskedPhone = await requestProfileOtp(challenge, context);

    if (!maskedPhone) {
      toast.error(
        'Não foi possível enviar o código por SMS. Tente novamente mais tarde.',
      );
      return true;
    }

    openOtpVerificationModal({
      maskedPhone,
      title: getOtpModalTitle(challenge),
      description: getOtpModalDescription(challenge),
      onVerify: (code) => verifyProfileOtp(code, challenge, context),
      onResend: () => requestProfileOtp(challenge, context),
      onSuccess: () => {
        const pending = pendingOtpChallengeRef.current;
        pendingOtpChallengeRef.current = null;

        if (pending) {
          void openProfileOtpChallenge(pending.challenge, pending.context);
        }
      },
    });

    return true;
  };

  const onSubmit = async (data: CustomerProfileFormData) => {
    if (!customerData?.customerId || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const profile = mapFormToProfile(data);

      const hasPhoneChanged =
        normalizePhoneForComparison(customerData.phone) !==
        normalizePhoneForComparison(data.phone);

      pendingOtpChallengeRef.current = null;

      const result = await updateCustomerProfile(customerData.customerId, profile);

      if (!result.success) {
        const profileOtpRequired = parseBoolean(
          getValue<boolean>(ValidationKey.VALIDATION_OTP_REQUIRED_PROFILE_UPDATE),
          false,
        );

        if (profileOtpRequired && result.status === 428 && result.otpChallenge) {
          const handled = await openProfileOtpChallenge(result.otpChallenge, {
            data,
            profile,
            hasPhoneChanged,
            otpHeaders: {},
          });

          if (handled) return;
        }

        toast.error(result.error);
        return;
      }

      await handleSuccessfulUpdate({ data, profile, hasPhoneChanged });
    } catch (error) {
      logger.error('Erro ao atualizar dados cadastrais:', error);
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : 'Confirme os dados e tente novamente',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    control,
    errors,
    isValid,
    isLoadingProfile,
    isSearchingCep,
    isSubmitting,
    handleCepBlur,
    handleClose,
    handleSubmit: handleSubmit(onSubmit),
    statesOptions: BRAZILIAN_STATES_OPTIONS,
    watchHasSocialName,
  };
};
